import React, {useEffect, useRef, useState} from "react";
import {
    Box,
    Button,
    Grid,
    Typography,
    keyframes,
    alpha,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {IncomingMessageBox} from "../components/IncomingMessageBox/IncomingMessageBox";
import {InteractionInterfaceBox} from "../components/InteractionInterfaceBox/InteractionInterfaceBox";
import {getScenarioByGuardian} from "../Services/ScenarioServices";
import {getScenarioNode} from "../Services/ScenarioNodesServices";
import FingerprintScanBtn from "../components/FingerprintScanBtn/FingerprintScanBtn";
import {
    appendGuardianMessageItem,
    appendHistoryText,
    appendUserOptionItem,
    formatDateTime,
    getRandomNumber,
    loadHistory,
    saveHistory
} from "../Helpers/Helpers";
import {HistoryItem} from "../types/history";
import {FingerprintLoading} from "../components/FingerprintLoading/FingerprintLoading";
import CommonDialog from "../components/Shared/CommonDialog/CommonDialog";
import {createInteractionLog} from "../Services/interactionLogService";
import styles from '../Styles/StartIdentifyScam.module.scss';
import {MobileBottomPanel} from "../components/MobileBottomPanel/MobileBottomPanel";

type RiskStatus = 'IsScam' | 'LikelyScam' | 'IsSafe' | 'LikelySafe';
type ScenarioNode = {
    id: number;
    text?: string;
    question?: string;
    nodeType: string;
    hint?: string;
    options: NodeOption[];
    riskStatus?: RiskStatus | null;
};

type NodeOption = {
    id: number;
    text: string;
    nextNodeId: number | null;
    explanation?: string;
    hint?: string;
};

export const StartIdentifyScam = () => {
    const [currentNode, setCurrentNode] = useState<ScenarioNode | null>(null);
    const [loading, setLoading] = useState(false);
    const [selectedGuardian, setSelectedGuardian] = useState<any>(null);
    const [started, setStarted] = useState<boolean>(false);
    const [openHint, setOpenHint] = useState(false);
    const [openUnknownEntity, setOpenUnknownEntity] = useState(false);
    const [notSupportMessage, setNotSupportMessage] = useState("");
    const [hintContent, setHintContent] = useState<string>("");
    const [openResetConfirm, setOpenResetConfirm] = useState(false);
    const [openAutoAssign, setOpenAutoAssign] = useState(false);
    const [openDisClaimer, setOpenDisClaimer] = useState(false);
    const [openUnkNowEntityDialog, setOpenUnkNowEntityDialog] = useState(false);
    const [interactionHistory, setInteractionHistory] = useState<HistoryItem[]>([]);
    const [openSaveDialog, setOpenSaveDialog] = useState(false);
    const [openEndConfirm, setOpenEndConfirm] = useState(false);
    const [fileName, setFileName] = useState("interaction-history");
    const fingerprintTimerRef = useRef<NodeJS.Timeout | null>(null);

    //-----------------------------------------------------------//
    const [fingerprintCheckpoint, setFingerprintCheckpoint] = useState<number | null>(null);
    const [openBackLimitDialog, setOpenBackLimitDialog] = useState(false);
    //-----------------------------------------------------------//

    type QuestionSnap = { nodeId: number; historyLength: number };
    const [qTrail, setQTrail] = useState<QuestionSnap[]>(() => {
        try {
            return JSON.parse(localStorage.getItem("qTrail") || "[]");
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem("qTrail", JSON.stringify(qTrail));
    }, [qTrail]);

    useEffect(() => {
        if (currentNode?.text === "Elemental Fingerprint Calculating") {
            setFingerprintCheckpoint(currentNode.id);
        }
    }, [currentNode]);

    useEffect(() => {
        const stored = localStorage.getItem("selectedGuardian");
        if (stored) {
            const guardian = JSON.parse(stored);
            setSelectedGuardian(guardian);
        }

        const isStarted = localStorage.getItem("scamStarted") === "true";
        setStarted(isStarted);
    }, []);

    useEffect(() => {
        const storedHistory = loadHistory();
        setInteractionHistory(storedHistory);
    }, []);

    useEffect(() => {
        return () => {
            if (fingerprintTimerRef.current) {
                clearTimeout(fingerprintTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!started) return;

        setLoading(true);
        const storedNodeId = localStorage.getItem("currentNodeId");
        const storedHistory = localStorage.getItem("interactionHistory");

        if (storedNodeId) {
            getScenarioNode(parseInt(storedNodeId)).then((res) => {
                setCurrentNode(res.data);
                localStorage.setItem("currentNodeType", res.data.nodeType);
                setLoading(false);
            });
            if (storedHistory) {
                setInteractionHistory(JSON.parse(storedHistory));
            }
        } else if (selectedGuardian.guardian) {
            getScenarioByGuardian(selectedGuardian.guardian).then((res) => {
                const rootNodeId = res.data.rootOptionId;
                getScenarioNode(rootNodeId).then((res1) => {
                    setCurrentNode(res1.data);
                    localStorage.setItem("currentNodeType", res1.data.nodeType);
                    setLoading(false);
                    localStorage.setItem("currentNodeId", rootNodeId.toString());
                    localStorage.setItem("interactionHistory", JSON.stringify([]));
                });
            });
        }
    }, [selectedGuardian, started]);

    useEffect(() => {
        if (currentNode?.text === "Elemental Fingerprint Calculating") {
            if (fingerprintTimerRef.current) {
                clearTimeout(fingerprintTimerRef.current);
            }

            const delay = getRandomNumber(1500, 4000);
            fingerprintTimerRef.current = setTimeout(() => {
                handleOptionClick(
                    currentNode.id + 1,
                    "Elemental Fingerprint Calculating",
                    currentNode
                );
            }, delay);
        }

        return () => {
            if (fingerprintTimerRef.current) {
                clearTimeout(fingerprintTimerRef.current);
                fingerprintTimerRef.current = null;
            }
        };
    }, [currentNode]);

    const handleStart = () => {
        if (!selectedGuardian?.guardian) {
            setOpenAutoAssign(true); // show popup
            return;
        } else {
            setOpenDisClaimer(true);
        }
    };

    const trimHistoryTo = (len: number) => {
        setInteractionHistory(prev => {
            const next = prev.slice(0, len);
            saveHistory(next);
            return next;
        });
    };
    const handleAutoAssignConfirm = () => {
        const guardian = {
            name: "Cyborg Bear",
            guardian: "CyborgBear",
            image: `/assets/cyborg-bear-1.png`,
            message: `You can call me "Cyborg Bear". How may I help you now?`
        };

        setSelectedGuardian(guardian);
        localStorage.setItem("selectedGuardian", JSON.stringify(guardian));

        appendHistoryText(setInteractionHistory, "Cyborg Bear has been assigned");

        setOpenAutoAssign(false);
        setOpenDisClaimer(true);
    };

    const handleAcceptConfirmDisclaimer = () => {
        console.log('handleAcceptConfirmDisclaimer');
        setOpenDisClaimer(false);
        setOpenUnkNowEntityDialog(true);
    }

    const handleAcceptConfirmUnkNowEntityDialog = () => {
        setOpenUnkNowEntityDialog(false);
        setStarted(true);
        localStorage.setItem("scamStarted", "true");
    }

    // const handlePreviousQuestion = async () => {
    //     if (qTrail.length === 0) return;

    //     // Lấy snapshot gần nhất
    //     const last = qTrail[qTrail.length - 1];
    //     const rest = qTrail.slice(0, -1);
    //     setQTrail(rest);
    //     localStorage.setItem("qTrail", JSON.stringify(rest));

    //     // Cắt Interaction History về đúng điểm thời gian đó
    //     trimHistoryTo(last.historyLength);

    //     // Quay về node câu hỏi trước
    //     localStorage.setItem("currentNodeId", String(last.nodeId));
    //     const res = await getScenarioNode(last.nodeId);
    //     setCurrentNode(res.data);
    // };
    // const handleGoBackOneStep = async () => {
    //     if (fingerprintTimerRef.current) {
    //         clearTimeout(fingerprintTimerRef.current);
    //         fingerprintTimerRef.current = null;
    //     }
    //
    //     if (qTrail.length === 0) return;
    //     // Lấy snapshot gần nhất
    //     const last = qTrail[qTrail.length - 1];
    //     const rest = qTrail.slice(0, -1);
    //
    //     // Cập nhật lại stack
    //     setQTrail(rest);
    //     localStorage.setItem("qTrail", JSON.stringify(rest));
    //
    //     // Cắt lịch sử tương tác tương ứng
    //     trimHistoryTo(last.historyLength);
    //
    //     // Quay lại node trước đó
    //     localStorage.setItem("currentNodeId", String(last.nodeId));
    //     const res = await getScenarioNode(last.nodeId);
    //     setCurrentNode(res.data);
    // };
    // const handleGoBackOneStep1 = async () => {
    //     if (fingerprintTimerRef.current) {
    //         clearTimeout(fingerprintTimerRef.current);
    //         fingerprintTimerRef.current = null;
    //     }
    //
    //     if (qTrail.length === 0) {
    //         setOpenBackLimitDialog(true);
    //         return;
    //     }
    //
    //     const last = qTrail[qTrail.length - 1];
    //
    //     if (fingerprintCheckpoint !== null && last.nodeId <= fingerprintCheckpoint) {
    //         setOpenBackLimitDialog(true);
    //         return;
    //     }
    //
    //     const rest = qTrail.slice(0, -1);
    //     setQTrail(rest);
    //     localStorage.setItem("qTrail", JSON.stringify(rest));
    //
    //     trimHistoryTo(last.historyLength);
    //
    //     localStorage.setItem("currentNodeId", String(last.nodeId));
    //     const res = await getScenarioNode(last.nodeId);
    //     setCurrentNode(res.data);
    // };
    const handleGoBackOneStep2 = async () => {
        if (fingerprintTimerRef.current) {
            clearTimeout(fingerprintTimerRef.current);
            fingerprintTimerRef.current = null;
        }

        if (qTrail.length === 0) {
            setOpenBackLimitDialog(true);
            return;
        }

        const last = qTrail[qTrail.length - 1];

        if (fingerprintCheckpoint !== null && last.nodeId <= fingerprintCheckpoint) {
            setOpenBackLimitDialog(true);
            return;
        }

        trimHistoryTo(last.historyLength);

        localStorage.setItem("currentNodeId", String(last.nodeId));
        const res = await getScenarioNode(last.nodeId);
        setCurrentNode(res.data);

        const empty: QuestionSnap[] = [];
        setQTrail(empty);
        localStorage.setItem("qTrail", JSON.stringify(empty));
    };

    const handleOptionClick = async (
        nextNodeId: number | null,
        selectedText: string,
        currentNode: ScenarioNode | null
    ) => {
        if (fingerprintTimerRef.current) {
            clearTimeout(fingerprintTimerRef.current);
            fingerprintTimerRef.current = null;
        }

        if (nextNodeId === null) {
            // alert("Kết thúc scenario.");
            setNotSupportMessage(selectedText);
            setOpenUnknownEntity(true);
            return;
        }

        if (currentNode) {
            // Luôn lưu lại snapshot trước khi chuyển sang node mới
            setQTrail(prev => [
                ...prev,
                {nodeId: currentNode.id, historyLength: interactionHistory.length}
            ]);
        }
        // if (currentNode && currentNode.nodeType === "guardian-message") {
        //     appendGuardianMessageItem(
        //         setInteractionHistory,
        //         "Guardian message",
        //         currentNode.question || currentNode.text || ""
        //     );
        // }
        // else if (selectedText !== "Elemental Fingerprint Calculating") {
        //     appendHistoryText(setInteractionHistory, selectedText);
        // }
        if (currentNode && currentNode.nodeType === "guardian-message") {
            appendGuardianMessageItem(
                setInteractionHistory,
                currentNode.id,
                currentNode.question || currentNode.text || ""
            );
        } else if (selectedText !== "Elemental Fingerprint Calculating") {
            // user chọn option
            const chosenOpt = currentNode?.options.find(o => o.text === selectedText);
            if (chosenOpt) {
                appendUserOptionItem(
                    setInteractionHistory,
                    currentNode!.id!,
                    chosenOpt.id,
                    `"${currentNode?.question ?? ""}" - ${chosenOpt.text}`
                );
            } else {
                // fallback text
                appendHistoryText(setInteractionHistory, selectedText);
            }
        }

        localStorage.setItem("currentNodeId", nextNodeId.toString());
        const response = await getScenarioNode(nextNodeId);
        setCurrentNode(response.data);
        localStorage.setItem("currentNodeType", response.data.nodeType);
    };

    const handleShowHint = (hint: string | null | undefined) => {
        if (hint && hint.trim() !== "") {
            setHintContent(hint);
            setOpenHint(true);
        }
    };

    const handleResetConfirm = () => {
        if (fingerprintTimerRef.current) {
            clearTimeout(fingerprintTimerRef.current);
            fingerprintTimerRef.current = null;
        }

        localStorage.removeItem("scamStarted");
        localStorage.removeItem("currentNodeId");
        //localStorage.removeItem("interactionHistory");
        const storedHistory = localStorage.getItem("interactionHistory");
        const arrHistory = storedHistory != null ? JSON.parse(storedHistory) : [];
        const newArrHistory = arrHistory.length > 0 ? [arrHistory[0]] : [];
        localStorage.setItem("interactionHistory", JSON.stringify(newArrHistory) ?? []);

        setStarted(false);
        setCurrentNode(null);
        setInteractionHistory(newArrHistory ?? []);
        setOpenResetConfirm(false);
    };

    const getRiskColors = (risk: RiskStatus, theme: any) => {
        const base =
            risk === 'IsScam' ? theme.palette.error.main :
                risk === 'LikelyScam' ? theme.palette.warning.main :
                    risk === 'LikelySafe' ? theme.palette.success.main :
                        theme.palette.success.main;

        return {
            base,
            bg: alpha(base, 0.12),
            text: theme.palette.getContrastText(base),
            shadow: alpha(base, 0.7),
            borderWeak: alpha(base, 0.3),
        };
    };

    const handleSaveToFile = async () => {
        const contents = interactionHistory
            .map((item, i) => `${i + 1}. ${item.time}: ${item.label}${item.detail ? " - " + item.detail : ""}`);
        const endTime = formatDateTime();
        const endAction = `${contents.length + 1}. ${endTime}: Save Action`;
        const downloadContent = [...contents, endAction].join("\n");

        // const jsonContent = buildInteractionLogJson(interactionHistory, "Save Action");
        const jsonContent = JSON.stringify([...contents, endAction]);
        await createInteractionLog({
            fileName: `${fileName || "interaction-history"}.json`,
            content: jsonContent
        });

        const blob = new Blob([downloadContent], {type: "text/plain;charset=utf-8"});
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName || "interaction-history"}.txt`;
        link.click();

        setOpenSaveDialog(false);
    };

    const handleEndClick = () => {
        setOpenEndConfirm(true);
    };

    const handleOpenSaveFromEnd = () => {
        setOpenEndConfirm(false);
        const now = new Date();
        const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
        setFileName(`interaction-history_${timestamp}`);
        setOpenSaveDialog(true);
    };

    const handleEndNotSave = async () => {
        if (fingerprintTimerRef.current) {
            clearTimeout(fingerprintTimerRef.current);
            fingerprintTimerRef.current = null;
        }

        // // 1. Tạo tên file tự động
        // const now = new Date();
        // const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
        // const autoFileName = `interaction-history-${timestamp}.txt`;
        //
        // // 2. Build nội dung file
        // const contents = interactionHistory
        //     .map((item, i) => `${i + 1}. ${item.time}: ${item.label}${item.detail ? " - " + item.detail : ""}`);
        // const endTime = formatDateTime();
        // const endAction = `${contents.length + 1}. ${endTime}: End Action`;
        // const downloadContent = [...contents, endAction].join("\n");
        //
        // // const jsonContent = buildInteractionLogJson(interactionHistory, "End Action");
        // const jsonContent = JSON.stringify([...contents, endAction]);
        // // 4. Gửi vào InteractionLog backend
        // await createInteractionLog({
        //     fileName: `${autoFileName}.json`,
        //     content: jsonContent
        // });
        //
        // const blob = new Blob([downloadContent], {type: "text/plain;charset=utf-8"});
        // const link = document.createElement("a");
        // link.href = URL.createObjectURL(blob);
        // link.download = autoFileName;
        // link.click();

        // 3. Reset interaction data trong localStorage
        localStorage.removeItem("scamStarted");
        localStorage.removeItem("currentNodeId");
        localStorage.removeItem("qTrail");
        localStorage.removeItem("interactionHistory");

        // 4. Reset state trong React
        setInteractionHistory([]);
        setQTrail([]);
        setCurrentNode(null);
        setStarted(false);

        // 5. Điều hướng về trang chọn guardian
        // window.location.href = "/chose-guardian";
    };


    return (
        <>
            <Grid container spacing={2}
                  className={styles.gridContainer}
            >
                <Grid
                    size={{xs: 12, md: 3, lg: 3}}
                    sx={{height: {md: "100%"}, display: {xs: "none", md: "block"}}}
                >

                    <IncomingMessageBox selectedGuardian={selectedGuardian}/>
                </Grid>

                <Grid
                    size={{xs: 12, md: 6, lg: 6}}
                    display="flex" justifyContent="center" alignItems="center" position={"relative"}
                    sx={{
                        // Mobile: stack question box on top, footerBar at bottom
                        flexDirection: {xs: "column", md: "row"},
                        justifyContent: {xs: "center", md: "center"},
                        alignItems: {xs: "stretch", md: "center"},
                        pt: {xs: 2, md: 0},
                    }}
                    className={styles.centerQuestionContainer}
                >
                    {!started &&
                        <>
                            <Box sx={{
                                display: "flex",
                                flexDirection: "column",
                            }}>
                                <FingerprintScanBtn onClick={handleStart}/>
                                <Box className={styles.poweredByMessageBox}>
                                    <Box className={`${styles.poweredByMessage} mt-1`}>
                                        Powered by The Wisdom Approach™
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    }

                    {/* WARNING BOX - chỉ hiện khi có riskStatus */}
                    {currentNode?.riskStatus && (
                        <>
                            <Box>
                                <Box className={`${styles.riskAlertWrapper} ${styles.riskAlert}`}
                                     sx={(theme) => {
                                         const c = getRiskColors(currentNode.riskStatus as RiskStatus, theme);
                                         return {
                                             // CSS variables để dùng trong keyframes
                                             '--alert-shadow': c.shadow,
                                             '--alert-border': c.base,
                                             '--alert-border-weak': c.borderWeak,
                                         };
                                     }}
                                     role="alert"
                                     aria-live="polite"
                                >
                                    <Typography
                                        variant="subtitle1"
                                        className={styles.riskText}
                                    >
                                        {currentNode.riskStatus === 'IsScam' && '⚠️ High Risk: SCAM Detected'}
                                        {currentNode.riskStatus === 'LikelyScam' && '⚠️ Likely Scam'}
                                        {currentNode.riskStatus === 'IsSafe' && '✓ No Critical Risk'}
                                        {currentNode.riskStatus === 'LikelySafe' && '✓ Likely Safe'}
                                        {currentNode.hint && (
                                            <HelpOutlineIcon
                                                onClick={() => handleShowHint(currentNode.hint)}
                                                className="muiButtonIcon"
                                                sx={{
                                                    position: 'absolute',
                                                    right: -40,
                                                    width: 30,
                                                    height: 30,
                                                    ml: 0.5,
                                                    cursor: 'pointer',
                                                    fill: '#D4AF37',
                                                    borderRadius: '50%',
                                                }}
                                            />)
                                        }
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}


                    {started && !loading && currentNode && (
                        <>
                            <Box className={`center-question-box ${styles.centerQuestionBox}`}>
                                {currentNode.text && currentNode.text !== 'Elemental Fingerprint Calculating' && (
                                    <>
                                        <Box
                                            className={`${styles.centerQuestionBoxWrapper} `}
                                        >
                                            <Typography
                                                variant="h6"
                                                className={`${styles.questionText} MuiSideBar background-transparent centerQuestionBoxWrapper`}
                                                component="span"
                                                dangerouslySetInnerHTML={{__html: currentNode.text}}
                                            />
                                        </Box>
                                    </>
                                )}

                                {currentNode.text && currentNode.text === 'Elemental Fingerprint Calculating' && (
                                    <>
                                        <FingerprintLoading
                                            label="Calculating..."
                                            size={72}
                                            speedSec={1.2}
                                            hint={currentNode.hint}
                                            handleShowHint={handleShowHint}
                                        />
                                    </>
                                )}

                                {currentNode.question && (
                                    <Box
                                        className={`${styles.centerQuestionBoxWrapper} `}
                                        sx={{textAlign: "center"}}
                                    >
                                        <Typography
                                            variant="h6"
                                            className={` MuiSideBar background-transparent 111 ${styles.questionText}`}
                                            component={'span'}
                                            dangerouslySetInnerHTML={{__html: currentNode.question}}
                                        >
                                        </Typography>
                                    </Box>
                                )}

                                <Box className={styles.optionContainer}>
                                    {currentNode.options.map((opt) => (
                                        <Box
                                            key={opt.id}
                                            className={styles.optionWrapper}
                                        >
                                            <Box sx={{
                                                position: {xs: "relative"},
                                            }}>
                                                <Button
                                                    className={`${styles.optionButton}`}
                                                    fullWidth
                                                    onClick={() => handleOptionClick(opt.nextNodeId, opt.text, currentNode)}
                                                    variant="outlined"
                                                >
                                                    {opt.text}
                                                    {opt.hint && opt.hint.trim() !== "" && (
                                                        <HelpOutlineIcon
                                                            className="muiButtonIcon"
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                handleShowHint(opt.hint)
                                                            }}
                                                            sx={{
                                                                position: "absolute",
                                                                right: {xs: -28, md: -28},
                                                                top: "50%",
                                                                transform: "translateY(-50%)",
                                                                cursor: "pointer",
                                                                zIndex: 1,
                                                            }}
                                                        />
                                                    )}
                                                </Button>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>

                                {currentNode.options.length === 0 && currentNode.nodeType !== 'end' && currentNode.text !== 'Elemental Fingerprint Calculating' && (
                                    <Box sx={{display: "flex", justifyContent: "center", position: "relative"}}>
                                        <Button
                                            onClick={() => handleOptionClick(currentNode.id + 1, "Continue", currentNode)
                                            }
                                            className={`centerButtons ${styles.continueButton}`}
                                            variant="outlined"
                                            sx={{
                                                fontWeight: "bold",
                                                fontFamily: "Orbitron, sans-serif",
                                                minWidth: 327.75
                                            }}
                                        >
                                            Continue
                                            {currentNode.hint && currentNode.hint.trim() !== "" && !currentNode.riskStatus && (
                                                <HelpOutlineIcon
                                                    className="muiButtonIcon"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleShowHint(currentNode.hint)
                                                    }}
                                                    sx={{
                                                        cursor: "pointer",
                                                        position: "absolute",
                                                        right: -28,
                                                    }}
                                                />
                                            )}
                                        </Button>
                                    </Box>
                                )}
                                {currentNode.options.length === 0 &&
                                    currentNode.nodeType === 'end' &&
                                    currentNode.text !== 'Elemental Fingerprint Calculating' && (
                                        <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                                            <Button
                                                onClick={handleEndClick}
                                                variant="outlined"
                                                className={`centerButton ${styles.endSaveButton}`}
                                                sx={{
                                                    mt: 3,
                                                    fontWeight: "bold",
                                                    fontFamily: "Orbitron, sans-serif",
                                                    minWidth: 120
                                                }}
                                            >
                                                End
                                            </Button>
                                        </Box>
                                    )}

                            </Box>
                            <Box
                                className={styles.footerBar}
                            >
                                {/* Nút Back bên trái */}
                                {qTrail.length > 0 ? (
                                    <Button
                                        variant="outlined"
                                        className={`centerButtons ${styles.backButton}`}
                                        onClick={handleGoBackOneStep2}
                                    >
                                        Back
                                    </Button>
                                ) : (
                                    <Box sx={{minWidth: {xs: 80, md: 120, lg: 120}, width: {xs: 80, md: 120}}}/>
                                )}

                                {/* Cụm nút giữa: Restart */}
                                <Button
                                    variant="outlined"
                                    className={`centerButtons ${styles.restartButton}`}
                                    onClick={() => setOpenResetConfirm(true)}
                                >
                                    Restart Scam Identification
                                </Button>

                                {currentNode.options.length === 0 &&
                                currentNode.nodeType === 'end' &&
                                currentNode.text !== 'Elemental Fingerprint Calculating' ? (
                                    <Button
                                        variant="outlined"
                                        className={`centerButton ${styles.endSaveButton}`}
                                        onClick={() => {
                                            setOpenSaveDialog(true);
                                            const now = new Date();
                                            const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
                                            setFileName(`interaction-history_${timestamp}`);
                                        }}
                                    >
                                        Save
                                    </Button>
                                ) : (
                                    <Box sx={{minWidth: {xs: 80, md: 120}, width: {xs: 80, md: 120}}}/>
                                )}
                            </Box>
                        </>
                    )}
                </Grid>

                <Grid size={{xs: 12, md: 3, lg: 3}}
                      sx={{height: "100%", display: {xs: "none", md: "block"}}}>
                    < InteractionInterfaceBox history={interactionHistory}/>
                </Grid>

                {/* Hint Dialog */
                }
                <CommonDialog
                    open={openHint}
                    title="Insight"
                    imageSrc={selectedGuardian?.image}
                    content={hintContent}
                    onClose={() => setOpenHint(false)}
                    showActions={false}
                />


                {/* Reset Confirmation Dialog */
                }
                <CommonDialog
                    open={openResetConfirm}
                    title="Confirm Reset"
                    imageSrc={selectedGuardian?.image}
                    content="Restart your session. All unsaved interactions will be removed."
                    onConfirm={handleResetConfirm}
                    onClose={() => setOpenResetConfirm(false)}
                    confirmText="Yes"
                    cancelText="Cancel"
                />

                <CommonDialog
                    open={openUnknownEntity}
                    title="Oops"
                    imageSrc={selectedGuardian?.image}
                    content={`The current demo does not yet support scam identification for ${notSupportMessage}. Currently, we are looking for funding and support to develop this capability. This would represent the first application to create a "logic-centric AI". Please contact us at SCZ.guardians@wisdomapproach.com if you would like to support us.`}
                    onClose={() => setOpenUnknownEntity(false)}
                    showActions={false}
                />

            </Grid>

            {/* Mobile-only: fixed bottom bar + slide-out drawers */
            }
            <MobileBottomPanel
                selectedGuardian={selectedGuardian}
                history={interactionHistory}
            />

            <CommonDialog
                open={openAutoAssign}
                title="Default Guardian Assigned"
                imageSrc={selectedGuardian?.image}
                content={
                    <>
                        <strong>
                            You haven&apos;t selected a guardian yet. The system will automatically assign{" "}
                            Cyborg Bear as your default guardian.
                        </strong>
                    </>
                }
                onConfirm={handleAutoAssignConfirm}
                onClose={handleAutoAssignConfirm}
                confirmText="OK"
                disableEscapeKeyDown
                centerActions
            />
            <CommonDialog
                open={openEndConfirm}
                title="End Session"
                imageSrc={selectedGuardian?.image}
                content={
                    <strong>
                        Would you like to save your interaction history before ending?
                    </strong>
                }
                onConfirm={() => {
                    handleOpenSaveFromEnd();
                    handleEndNotSave();
                }}
                onClose={() => {
                    handleEndNotSave();
                    setOpenEndConfirm(false);
                }}
                confirmText="Save"
                cancelText="No"
            />

            <CommonDialog
                open={openSaveDialog}
                title="Save Interaction History"
                imageSrc={selectedGuardian?.image}
                content={
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <Typography>Enter file name:</Typography>
                        <input
                            type="text"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                            style={{padding: "8px", fontSize: "14px"}}
                        />
                    </Box>
                }
                onConfirm={handleSaveToFile}
                onClose={() => setOpenSaveDialog(false)}
                confirmText="Save"
                cancelText="Cancel"
            />

            <CommonDialog
                open={openDisClaimer}
                title="Disclaimer"
                // dialogClassName="disclaimer-dialog"
                imageSrc={selectedGuardian?.image}
                // imageSrc="/assets/cyborg-bear-1.png"
                content={
                    <Box className="disclaimer-dialog">
                        <strong>
                            Disclaimer! Scam Protect offers the most accurate results when the recommended steps are
                            followed properly. It is likely that deviation of inputs can occur.
                            As such, please utilize scam protect's scam identification results accordingly by treating
                            it as advice and guidance.
                            Remember that you are responsible for your actions regardless of the advice given.
                        </strong>
                    </Box>
                }
                onConfirm={handleAcceptConfirmDisclaimer}
                onClose={handleAcceptConfirmDisclaimer}
                confirmButtonClassName="disclaimer-ok-button"
                confirmText="Next"
                disableEscapeKeyDown
                centerActions
                showCancel={false}
            />
            <CommonDialog
                open={openUnkNowEntityDialog}
                title="Note"
                // dialogClassName="disclaimer-dialog"
                imageSrc={selectedGuardian?.image}
                // imageSrc="/assets/cyborg-bear-1.png"
                content={
                    <Box>
                        <strong>
                            Currently, the demo supports scam identification for "Misrepresentation
                            of <u>Known</u> Entities".
                            We are looking for funding and support to develop a new capability to address
                            "Misrepresentation of <u>Unknown</u> Entities".
                            This would represent the first application to create a "logic-centric AI" using The Wisdom
                            Approach.
                            Please contact us at SCZ.guardians@gmail.com if you would like to support us.
                        </strong>
                    </Box>}
                onConfirm={handleAcceptConfirmUnkNowEntityDialog}
                onClose={handleAcceptConfirmUnkNowEntityDialog}
                confirmButtonClassName="disclaimer-ok-button"
                confirmText="Next"
                disableEscapeKeyDown
                centerActions
                showCancel={false}
            />
            <CommonDialog
                open={openBackLimitDialog}
                title="Notice"
                imageSrc={selectedGuardian?.image}
                content="This is the furthest you can go back."
                onClose={() => setOpenBackLimitDialog(false)}
                showActions={false}
                confirmText="Save"
                cancelText="Cancel"
            />
        </>
    )
        ;
};
